import styled from 'styled-components';
import { rgba } from 'polished';
import { COLORS } from '@/constants/styles';
import { ExclamationCircle } from '@/components/uielements/icon/exclamation-circle';
import { StyledPrimaryButton } from '@/components/uielements/primaryButton/styles';
import { useDispatch, useSelector } from '@/redux';
import { selectBasketSlice } from '@/redux/basket';
import { selectConveyance } from '@/redux/conveyanceMode/reducer';
import menuActions from '@/redux/menu/actions';
import { selectMenuSlice } from '@/redux/menu/reducer';

const Container = styled.div`
  margin: var(--size-3) var(--size-6);
  padding: 12px;
  border: 1px solid ${COLORS.PRIMARY_WARNING};
  background-color: ${rgba(COLORS.PRIMARY_WARNING, 0.1)};
  border-radius: 8px;
  display: flex;
  gap: 8px;
`;

const Icon = styled(ExclamationCircle)`
  color: ${COLORS.PRIMARY_WARNING};
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  margin-top: 2px;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Title = styled.span`
  color: ${COLORS.DARKORANGE};
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
`;

const Description = styled.span`
  color: ${COLORS.DARKORANGE};
  font-size: 12px;
  line-height: 19.5px;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
`;

/**
 * TEAM-14909: `refreshBasketMenuSaga` re-fetches the basket's menu (on basket
 * hydration and on a pickup/delivery time change) with 3 automatic retries. When
 * all of them fail, the reducer records `basketMenuError` — without this notice the
 * cart/checkout summary just renders off stale or empty menu data with no signal.
 * Surface the failure with a plain-language message and a manual retry that
 * re-dispatches `refreshBasketMenu`.
 */
export function BasketMenuErrorNotice() {
  const dispatch = useDispatch();
  const { basketMenuError } = useSelector(selectMenuSlice);
  const { content: basketContent } = useSelector(selectBasketSlice);
  const { time_wanted } = useSelector(selectConveyance);

  if (!basketMenuError) {
    return null;
  }

  const locationId = basketContent?.location?.id;

  const handleRetry = () => {
    if (locationId == null) {
      return;
    }
    dispatch(menuActions.refreshBasketMenu(locationId, time_wanted ?? undefined));
  };

  return (
    <Container>
      <Icon title="Refresh Warning" />
      <Content>
        <Title>We couldn&apos;t refresh your order</Title>
        <Description>
          Something went wrong updating your cart, so your items and totals may be out of date.
          Please try again.
        </Description>
        <ButtonRow>
          <StyledPrimaryButton size="small" type="button" onClick={handleRetry}>
            Try again
          </StyledPrimaryButton>
        </ButtonRow>
      </Content>
    </Container>
  );
}
